import React, { useState, memo, useEffect } from 'react';
import { Button } from 'primereact/button';

import GlobalLoader from 'components/GlobalLoader/GlobalLoader';
import ThreadHeading from './ThreadHeading';
import ConversationBetween from './ConversationBetween';
import ThreadActions from './ThreadActions';
import AssignFirmUserResolution from './AssignFirmUserResolution';
import ConversationList from './ConversationList';
import ReplyInput from './ReplyInput';
import InviteDialog from './InviteDialog';
import Sidebar from '../Sidebar/Sidebar';

import { useMessageDetailsPage } from '../../hooks/useMessageDetailsPage';
import { useCaseName } from '../../hooks/useCaseName';
import { handleRedirection } from 'utils/utils';

function ThreadLoadingState() {
  return (
    <div
      className="px-2 d-flex justify-content-center align-items-center"
      style={{ minHeight: '60vh' }}
      role="main"
      aria-label="Loading thread"
      aria-busy="true"
    >
      <GlobalLoader />
      <div className="d-flex flex-column align-items-center gap-2">
        <span className="text-muted">Loading thread...</span>
      </div>
    </div>
  );
}

function ThreadErrorState({ is404, onRetry, onBack }) {
  return (
    <div className="px-2" role="main" aria-label="Error">
      <GlobalLoader />
      <div className="d-flex justify-content-center align-items-center flex-column" style={{ minHeight: '60vh' }}>
        <i
          className={`pi ${is404 ? 'pi-lock' : 'pi-exclamation-triangle'}`}
          style={{ fontSize: '3rem', color: 'var(--red-500)' }}
          aria-hidden="true"
        />
        <h5 className="mt-3 mb-1">{is404 ? 'Thread not found or access denied' : 'Error loading thread'}</h5>
        <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
          {is404
            ? 'This thread may have been deleted or you do not have permission to view it.'
            : 'Something went wrong while loading this thread. Please try again.'}
        </p>
        <div className="d-flex gap-2">
          {!is404 && (
            <Button
              label="Retry"
              icon="pi pi-refresh"
              className="p-button-primary p-button-secondary p-button-outlined"
              onClick={onRetry}
            />
          )}
          <Button label="Go Back" icon="pi pi-arrow-left" className="p-button-secondary" onClick={onBack} />
        </div>
      </div>
    </div>
  );
}

function MessageDetailsPage() {
  const {
    messageDetails,
    totalCount,
    isLoadingDetails,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetchAll,
    isErrorDetails,
    errorDetails,
    hasPermission,
    backUrl,
    isFromClientPortal,
    isFromLead,
    showInviteDialog,
    setShowInviteDialog,
    caseUserClients,
    handleMarkAsUnRead,
    handleAddSelf,
    loadCaseContacts,
    history,
  } = useMessageDetailsPage();

  // Layout-only state — sidebar visibility is a pure UI concern
  const [showSideBar, setShowSideBar] = useState(false);

  useEffect(() => {
    messageDetails?.case?.case_id && !isFromClientPortal && loadCaseContacts(messageDetails?.case?.case_id);
  }, [messageDetails?.case?.case_id]);

  // All CaseName business logic lives here so it can be shared across layout sections
  const {
    newMsg,
    showCaseSearch,
    selectedCase,
    caseError,
    isLoadingCaseUsers,
    conflictUsers,
    showConflictDialog,
    isMutating,
    handleEditCase,
    handleCaseDropdownChange,
    resetCaseSearch,
    handleConfirmCaseChange,
    doChangeCase,
    handleConflictDialogHide,
    handleInviteClick,
    handleSidebarToggle,
    downloadSecureMessagePDF,
  } = useCaseName({ messageDetails, setShowInviteDialog, setShowSideBar });

  // Shared props spread across ThreadActions sections
  const caseNameSharedProps = {
    messageDetails,
    newMsg,
    showCaseSearch,
    selectedCase,
    caseError,
    isLoadingCaseUsers,
    conflictUsers,
    showConflictDialog,
    isMutating,
    handleEditCase,
    handleCaseDropdownChange,
    resetCaseSearch,
    handleConfirmCaseChange,
    doChangeCase,
    handleConflictDialogHide,
    handleSidebarToggle,
    downloadSecureMessagePDF,
    showSideBar,
    handleMarkAsUnRead,
    hasPermission,
  };

  if (isLoadingDetails) return <ThreadLoadingState />;

  if (isErrorDetails) {
    return (
      <ThreadErrorState
        is404={errorDetails?.response?.status === 404}
        onRetry={refetchAll}
        onBack={() => handleRedirection(history, backUrl)}
      />
    );
  }

  return (
    <div className="px-2" role="main" aria-label="Message thread details">
      <GlobalLoader />

      {/* Row 1: Thread title + Invite Users */}
      <ThreadHeading
        messageDetails={messageDetails}
        handleMarkAsUnRead={handleMarkAsUnRead}
        isFromClientPortal={isFromClientPortal}
        hasPermission={hasPermission}
        onInviteClick={handleInviteClick}
      />

      {/* Row 2: Conversation Between (left) + Case info (right) */}
      {!isFromClientPortal && messageDetails && (
        <ConversationBetween
          messageDetails={messageDetails}
          isFromLead={isFromLead}
          caseInfoSlot={<ThreadActions {...caseNameSharedProps} section="case-info" />}
        />
      )}

      {/* Row 3: Assign for Resolution (left) + action buttons (right) */}
      {messageDetails && !isFromClientPortal && (
        <AssignFirmUserResolution
          isFromLead={isFromLead || messageDetails?.is_lead}
          messageDetails={messageDetails}
          hasPermission={hasPermission}
          actionsSlot={<ThreadActions {...caseNameSharedProps} section="action-buttons" />}
        />
      )}

      <div className="inner-conversation pb-3">
        <div className="row">
          <div className={showSideBar ? 'col-md-8 col-sm-12' : 'col-md-12'}>
            <div className="card card-bg">
              <div className="card-body">
                <ConversationList
                  messageDetails={messageDetails}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  fetchNextPage={fetchNextPage}
                  totalCount={totalCount}
                />
                <ReplyInput messageDetails={messageDetails} hasPermission={hasPermission} addUserHandler={handleAddSelf} />
              </div>
            </div>
          </div>

          {messageDetails && showSideBar && (
            <div className="col-md-4 col-sm-12 p-md-0 mt-2 mt-md-0">
              <div className="card-bg p-2 h-100" style={{ display: 'flex', flexDirection: 'column', position: 'sticky', top: 0 }}>
                <Sidebar messageDetails={messageDetails} hasPermission={hasPermission} />
              </div>
            </div>
          )}
        </div>
      </div>

      {showInviteDialog && (
        <InviteDialog
          messageDetails={messageDetails}
          caseUserClients={caseUserClients}
          showInviteDialog={showInviteDialog}
          setShowInviteDialog={setShowInviteDialog}
        />
      )}
    </div>
  );
}

export default memo(MessageDetailsPage);
